import React, { useEffect, useState, useRef, useCallback } from "react";
import { Clock, RefreshCw } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const WARN_WINDOW_MS = 5 * 60 * 1000; // show the heads-up 5 minutes before the token expires

export function SessionExpiryWarning() {
  const { user, refreshUser } = useAuth();
  const [show, setShow] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [renewing, setRenewing] = useState(false);
  const timers = useRef([]);

  const clearTimers = () => {
    timers.current.forEach((t) => clearTimeout(t) || clearInterval(t));
    timers.current = [];
  };

  const staySignedIn = useCallback(async () => {
    setRenewing(true);
    try { await refreshUser(); setShow(false); } catch { /* next API 401 will redirect */ }
    finally { setRenewing(false); }
  }, [refreshUser]);

  useEffect(() => {
    clearTimers();
    setShow(false);
    const exp = user && typeof user.session_expires_at === "number" ? user.session_expires_at * 1000 : null;
    if (!exp) return;

    const startCountdown = () => {
      setShow(true);
      const tick = () => {
        const left = Math.max(0, Math.round((exp - Date.now()) / 1000));
        setSecondsLeft(left);
        if (left <= 0) clearTimers();
      };
      tick();
      const iv = setInterval(tick, 1000);
      timers.current.push(iv);
    };

    const msUntilWarn = exp - WARN_WINDOW_MS - Date.now();
    if (msUntilWarn <= 0) {
      if (Date.now() < exp) startCountdown(); // already inside the warning window
    } else {
      timers.current.push(setTimeout(startCountdown, msUntilWarn));
    }
    return clearTimers;
  }, [user && user.session_expires_at]);

  if (!show || secondsLeft <= 0) return null;
  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;

  return (
    <div
      data-testid="session-expiry-warning"
      className="fixed bottom-4 right-4 z-[60] max-w-sm flex items-center gap-3 rounded-xl border border-amber-500/30 bg-slate-900/95 backdrop-blur px-4 py-3 shadow-xl"
    >
      <Clock className="w-5 h-5 text-amber-400 shrink-0" />
      <div className="text-sm text-slate-200 leading-tight">
        <div className="font-semibold text-amber-200">Your session is about to expire</div>
        <div className="text-xs text-slate-400">
          You’ll be signed out in <span className="font-mono text-slate-200" data-testid="session-expiry-countdown">{mins}:{String(secs).padStart(2, "0")}</span>. Save any work in progress.
        </div>
      </div>
      <button
        onClick={staySignedIn}
        disabled={renewing}
        data-testid="session-stay-signed-in"
        className="ml-1 flex items-center gap-1.5 shrink-0 bg-sky-600 hover:bg-sky-500 disabled:opacity-60 text-white text-xs font-semibold rounded-lg px-3 py-1.5 transition-colors"
      >
        <RefreshCw className={`w-3.5 h-3.5 ${renewing ? "animate-spin" : ""}`} /> Stay signed in
      </button>
    </div>
  );
}
